import { useConnection } from 'wagmi';
import { StakingContract } from 'utils/constants/abi/StakingContract';
import { formatUnits, erc20Abi } from 'viem';
import { stakingPoolListQueryOptions } from 'structure/staking/hooks/useStakingAPI';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { StakingPool } from '../../../../api/StakingAPI';
import { getPublicClient } from '@wagmi/core';
import { wagmiConfig } from 'structure/providers/wagmiConfig';
import { queryKeys } from 'structure/pv2/vault/configs/queryKeys';

function groupByChainId(pools: StakingPool[]) {
  const map = new Map<number, StakingPool[]>();
  for (const pool of pools) {
    const arr = map.get(pool.chainId) ?? [];
    arr.push(pool);
    map.set(pool.chainId, arr);
  }
  return map;
}

function buildContractCalls(props: {
  chainId: number;
  userAddress: `0x${string}`;
  stakingContractAddress: `0x${string}`;
  stakingTokenAddress: `0x${string}`;
}) {
  const { chainId, userAddress, stakingContractAddress, stakingTokenAddress } =
    props;

  return [
    {
      address: stakingContractAddress,
      abi: StakingContract,
      functionName: 'userInfo',
      args: [userAddress],
      chainId: chainId,
    },
    {
      address: stakingContractAddress,
      abi: StakingContract,
      functionName: 'pendingFor',
      args: [userAddress],
      chainId: chainId,
    },
    {
      address: stakingTokenAddress,
      abi: erc20Abi,
      functionName: 'balanceOf',
      args: [stakingContractAddress],
      chainId: chainId,
    },
  ];
}

async function fetchDataByChain(props: {
  stakingPools: StakingPool[];
  userAddress: `0x${string}`;
  chainId: number;
}) {
  const { stakingPools, userAddress, chainId } = props;

  const contractCalls = stakingPools
    .map((pool) => {
      return buildContractCalls({
        chainId: pool.chainId,
        userAddress: userAddress,
        stakingContractAddress: pool.stakingContractAddress as `0x${string}`,
        stakingTokenAddress: pool.depositTokenAddress as `0x${string}`,
      });
    })
    .flat();

  const publicClient = getPublicClient(wagmiConfig, { chainId });

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const multiCalls = await publicClient.multicall({
    contracts: contractCalls,
    allowFailure: true,
    authorizationList: undefined,
  });

  let totalInUSD = 0;

  for (let i = 0; i < multiCalls.length; i = i + 3) {
    const k = i / 3;
    const userData = multiCalls[i];
    const userPendingRewardsAmount = multiCalls[i + 1];
    const poolTotalStakedAmount = multiCalls[i + 2];

    if (
      userData.status !== 'success' ||
      userPendingRewardsAmount.status !== 'success' ||
      poolTotalStakedAmount.status !== 'success'
    ) {
      continue;
    }

    const userStakedAmount = (
      userData.result as unknown as readonly unknown[]
    )[0] as bigint;
    const stakingPool = stakingPools[k];
    const totalStaked = Number(
      formatUnits(
        (poolTotalStakedAmount.result as bigint) || BigInt(0),
        stakingPool.depositTokenDecimals
      )
    );
    const assetPrice = Number(stakingPool.totalValueLockedUSD) / totalStaked;

    const tokenAmount =
      Number(formatUnits(userStakedAmount, stakingPool.depositTokenDecimals)) +
      Number(
        formatUnits(
          (userPendingRewardsAmount.result as bigint) || BigInt(0),
          stakingPool.depositTokenDecimals
        )
      );

    totalInUSD += tokenAmount * assetPrice;
  }

  return totalInUSD;
}

export const useUserTotalStakeAssets = () => {
  const connection = useConnection();
  const qc = useQueryClient();

  return useQuery({
    queryKey: queryKeys.userTotalStakedAssets({
      userAddress: connection.address as `0x${string}`,
    }),
    enabled: !!connection.address,
    queryFn: async () => {
      const stakingPools = await qc.ensureQueryData(
        stakingPoolListQueryOptions()
      );

      const groups = groupByChainId(stakingPools);

      let totalUsd = 0;
      for (const [chainId, pool] of groups) {
        const usd = await fetchDataByChain({
          stakingPools: pool,
          userAddress: connection.address as `0x${string}`,
          chainId: chainId,
        });

        totalUsd += usd;
      }

      return totalUsd;
    },
  });
};
